import { PureComponent, createRef } from 'react'
import { Link } from 'react-router-dom'

import Checkbox from '@material-ui/core/Checkbox'
import CircularProgress from '@material-ui/core/CircularProgress'
import Paper from '@material-ui/core/Paper'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import { grey, red } from '@material-ui/core/colors'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import classNames from 'classnames'
import moment from 'moment'
import { parse } from 'query-string'

import { getPath } from '~/utils'
import { currencySymbols } from '../../constants'
import { debounce } from '../../tools/utils'
import ShippingInfoManager from '../containers/shipping-info-manager'
import FulfillmentSplitDialog from './fulfillment/fulfillment-split'
import FulfillmentTable from './fulfillment/fulfillment-table'
import SaleCSVExportSelector from './sale-csv-export-selector'
import GenerateTaxInvoiceModal from './sale-generate-invoices'
import ProcessInsufficientStockModal from './sale-process-insufficient-stock'
import SalesTableToolbar from './sales-table-toobar'

import 'moment/locale/en-gb'
import 'moment/locale/th'

const styles = () => ({
    root: {
        width: '100%',
    },
    table: {
        width: '100%',
        tableLayout: 'fixed',
    },
    tableHeader: {
        borderTop: '1px solid ' + grey[200],
    },
    tableCell: {
        padding: '3px 12px',
        wordWrap: 'break-word',
        fontSize: '0.85rem',
    },
    noSalesMessage: {
        textAlign: 'center',
        padding: 75,
    },
    noSalesText: {
        color: grey[400],
    },
    saleRowWithFulfillments: {
        borderBottom: 'none',
        '& $tableCell': {
            borderBottom: 'none',
        },
    },
    tableText: {
        fontSize: '0.85rem',
    },
    saleLink: {
        textDecoration: 'none',
        color: 'inherit',
    },
    saleImage: {
        width: 40,
    },
    saleTotal: {
        textAlign: 'center',
    },
    numItems: {
        color: grey[600],
    },
    shippingColumn: {
        textAlign: 'center',
    },
    status: {
        textAlign: 'center',
    },
    statusUpdatedTime: {
        color: grey[600],
    },
    channel: {
        textAlign: 'center',
    },
    channelIcon: {
        alignSelf: 'flex-start',
        width: 25,
    },
    shipByDate: {
        color: grey[600],
        border: `1px solid ${grey[300]}`,
        borderRadius: '4px',
        fontSize: 10,
        padding: 3,
    },
    shipByDateOverdue: {
        border: `1px solid ${red[100]}`,
        color: red[400],
    },
    tableContainer: {
        maxHeight: 'calc(100vh - 170px)',
    },
})

const useStyles = makeStyles(styles)

const columns = [
    { id: 'image', label: '', sort: false, centerAlign: false, width: 70 },
    {
        id: 'sale_id',
        label: gettext('Sale ID'),
        sort: false,
        centerAlign: false,
        minWidth: 100,
    },
    {
        id: 'location',
        label: gettext('Location'),
        sort: false,
        centerAlign: false,
        minWidth: 100,
    },
    {
        id: 'shop_name',
        label: gettext('Shop name'),
        sort: false,
        centerAlign: false,
        minWidth: 100,
    },
    {
        id: 'sale_total',
        label: gettext('Sale total'),
        sort: false,
        centerAlign: true,
        width: 110,
    },
    {
        id: 'shipping_provider',
        label: gettext('Shipping'),
        sort: false,
        centerAlign: true,
        width: 130,
    },
    {
        id: 'is_tax_invoice_requested',
        label: gettext('Tax Invoice'),
        sort: false,
        centerAlign: true,
        width: 95,
    },
    {
        id: 'status',
        label: gettext('Status'),
        sort: false,
        centerAlign: true,
        minWidth: 100,
    },
    {
        id: 'channel',
        label: gettext('Channel'),
        sort: false,
        centerAlign: true,
        width: 90,
    },
]

function SaleTableHead({ selectAllState, onSelectAllClick }) {
    const classes = useStyles()

    return (
        <TableHead className={classes.tableHeader}>
            <TableRow>
                <TableCell className={classes.tableCell} style={{ width: 60 }}>
                    <Checkbox
                        indeterminate={selectAllState === 'indeterminate'}
                        checked={selectAllState === 'checked'}
                        size="small"
                        onChange={onSelectAllClick}
                    />
                </TableCell>
                {columns.map((column) => {
                    return (
                        <TableCell
                            key={column.id}
                            padding={column.disablePadding ? 'none' : 'default'}
                            style={{
                                textAlign: column.centerAlign && 'center',
                                width: column.width ? column.width : 'auto',
                                minWidth: column.minWidth ? column.minWidth : 0,
                            }}
                            className={classes.tableCell}
                        >
                            {column.label}
                        </TableCell>
                    )
                }, this)}
            </TableRow>
        </TableHead>
    )
}

const pendingStatuses = new Set(['pending', 'erp_pending'])

class Sales extends PureComponent {
    constructor(props) {
        super(props)

        moment.locale(props.language)

        this.state = {
            selected: new Set(),
            rowsPerPage: 50,
            sales: [],
            count: 0,
            page: 0,
            maxPageFetched: 0,
            pendingCount: 0,
            loading: true,
            shippingInfoManagerOpen: false,
            filterOptions: [],
            filterValues: {
                date_created_after: moment().subtract(13, 'days'),
                date_created_before: moment(),
                id_value: '',
                fulfillment_tracking: '',
                search: '',
            },
            exportSelectorOpen: false,
            exportParamsString: '',
            pageWithAllSelect: {},

            isSelectAll: false,
            selectedSalePKs: new Set(),
            unselectedSalePKs: new Set(),
            selectionAnchorIndex: -1,
            orderBy: null,
            showFulfillmentView: false,
            splitFulfillmentSale: null,
            currentAbortController: null,
        }

        this.salesContainerRef = createRef()
        this.fetchSaleDataDebounced = debounce(this.fetchSaleData, 500)
    }

    componentDidMount() {
        this.fetchFilterOptions(() => {
            this.fetchSaleData(this.getDataURL(), false, 0)
        })
    }

    componentWillUnmount() {
        this.state.currentAbortController?.abort()
    }

    filtersToQueryString = (filters = null) => {
        let filtersToUse = filters || this.state.filterValues

        if (filtersToUse.id_value) {
            filtersToUse = { id_value: filtersToUse.id_value }
        } else if (filtersToUse.fulfillment_tracking) {
            filtersToUse = { fulfillment_tracking: filtersToUse.fulfillment_tracking }
        }

        return Object.entries(filtersToUse)
            .filter(([, option]) => Boolean(option))
            .map(([fieldLookup, option]) => {
                if (moment.isMoment(option)) {
                    return `${fieldLookup}=${encodeURIComponent(option.format('YYYY-MM-DD'))}`
                } else {
                    return `${fieldLookup}=${encodeURIComponent(option.value || option)}`
                }
            })
            .join('&')
    }

    fetchSaleData = async (url, replace, page = null, filtersToUse = null) => {
        this.state.currentAbortController?.abort()

        const abortController = new AbortController()
        this.setState({ currentAbortController: abortController, loading: true })

        if (!url.includes('?')) {
            const query = this.filtersToQueryString(filtersToUse)
            if (query) {
                url += '?' + query
            }
        }
        if (this.state.orderBy && !url.includes('order_by')) {
            if (url.includes('?')) {
                url += '&order_by=' + this.state.orderBy
            } else {
                url += '?order_by=' + this.state.orderBy
            }
        }
        if (this.state.showFulfillmentView) {
            if (url.includes('?')) {
                url += '&expand=fulfillments'
            } else {
                url += '?&expand=fulfillments'
            }
        }

        try {
            const res = await fetch(url, { signal: abortController.signal })
            if (!res.ok) {
                throw new Error('Failed to fetch sales data')
            }

            const data = await res.json()
            const sales = replace ? data.results : [...this.state.sales, ...data.results]

            const newState = {
                count: data.count,
                next: data.next,
                previous: data.previous,
                sales: sales,
            }

            if (page === 0) {
                newState['page'] = 0
                newState['maxPageFetched'] = 0
                newState['pageWithAllSelect'] = {}
            } else if (page > 0) {
                newState['page'] = page
            }

            this.setState(newState)
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error(error)
            }
        } finally {
            this.setState((prevState) => ({
                ...prevState,
                loading: false,
                currentAbortController:
                    prevState.currentAbortController === abortController
                        ? null
                        : prevState.currentAbortController,
            }))
        }
    }

    fetchFilterOptions = (callback) => {
        const { salesFilterValuesAPIURL, currentUserID } = this.props
        let url = salesFilterValuesAPIURL.replace('user_pk', currentUserID)
        const { date_created_after, date_created_before } = this.state.filterValues
        url += `?date_created_after=${date_created_after.format(
            'YYYY-MM-DD'
        )}&date_created_before=${date_created_before.format('YYYY-MM-DD')}`

        fetch(url).then((response) => {
            if (response.ok) {
                response.json().then((data) => {
                    const filterValues = { ...this.state.filterValues }
                    data.forEach((field) => {
                        if (field.lookup === 'status') {
                            const pendingOption = field.values.find((v) => {
                                return pendingStatuses.has(v.value)
                            })
                            filterValues[field.lookup] = pendingOption
                        } else if (field.lookup === 'sale_type') {
                            filterValues[field.lookup] = field.values.find(
                                (option) => option.value === 'order'
                            )
                        } else {
                            filterValues[field.lookup] = null
                        }
                    })
                    this.setState(
                        {
                            filterOptions: data,
                            filterValues,
                        },
                        callback
                    )
                })
            }
        })
    }

    getDataURL = () => {
        // In the future this should fetch sales for shops
        // the user has permission for
        const { accountOwnerID, salesAPIURL } = this.props
        return salesAPIURL.replace('user_pk', accountOwnerID)
    }

    handleExportSelected = () => {
        this.setState({ exportSelectorOpen: true })
    }

    handleShipSelected = () => {
        this.handleOpenShippingInfoManager()
    }

    handleOpenShippingInfoManager = () => {
        this.setState({
            shippingInfoManagerOpen: true,
            salesFilter: this.getSalesFilter(),
        })
    }

    handleCloseShippingInfoManager = (isSubmitted) => {
        this.setState({ shippingInfoManagerOpen: false })
        if (isSubmitted) {
            this.clearSelectionAndRefetch()
        }
    }

    clearSelectionAndRefetch = () => {
        // refetch sales and clear selected
        this.fetchSaleData(this.getDataURL(), true, 0)
        this.setState({
            isSelectAll: false,
            selectedSalePKs: new Set(),
            unselectedSalePKs: new Set(),
            selectionAnchorIndex: -1,
        })
    }

    handleSort = (orderBy) => {
        if (this.salesContainerRef.current) {
            // reset scroll to top
            this.salesContainerRef.current.scrollTop = 0
        }
        this.setState({ orderBy }, () => {
            this.fetchSaleData(this.getDataURL(), true, 0)
        })
    }

    handleSelectAllClick = () => {
        this.setState((prevState) => ({
            ...prevState,
            isSelectAll: !prevState.isSelectAll,
            selectedSalePKs: new Set(),
            unselectedSalePKs: new Set(),
            selectionAnchorIndex: -1,
        }))
    }

    handleCheckboxClick = (event, saleID, rowIndex) => {
        const checked = event.target.checked
        const { selectedSalePKs, unselectedSalePKs, isSelectAll, selectionAnchorIndex } = this.state
        if (isSelectAll) {
            if (checked) {
                unselectedSalePKs.delete(saleID)
            } else {
                unselectedSalePKs.add(saleID)
            }
            this.setState({ unselectedSalePKs: new Set(unselectedSalePKs) })
        } else {
            const newState = {}
            if (event.shiftKey) {
                if (selectionAnchorIndex >= 0) {
                    // range selection
                    selectedSalePKs.clear()
                    const startIndex = Math.min(selectionAnchorIndex, rowIndex)
                    const stopIndex = Math.max(selectionAnchorIndex, rowIndex)
                    this.state.sales.forEach((sale, index) => {
                        if (index >= startIndex && index <= stopIndex) {
                            selectedSalePKs.add(sale.id)
                        }
                    })
                }
            } else {
                if (checked) {
                    selectedSalePKs.add(saleID)
                    newState.selectionAnchorIndex = rowIndex
                } else {
                    selectedSalePKs.delete(saleID)
                    newState.selectionAnchorIndex = -1
                }
            }
            this.setState({ ...newState, selectedSalePKs: new Set(selectedSalePKs) })
        }
    }

    handleChangePage = (page) => {
        const pageChangeURL = page > this.state.page ? this.state.next : this.state.previous
        const needsFetch = page > this.state.maxPageFetched
        if (needsFetch) {
            this.setState({ maxPageFetched: page }, () => {
                this.fetchSaleData(pageChangeURL, false, page)
            })
        } else {
            this.setState({ page })
        }
    }

    loadNextPage = () => {
        const { page, next } = this.state
        if (next) {
            const nextPage = page + 1
            this.handleChangePage(nextPage)
        }
    }

    isSelected = (salePK) => {
        const { selectedSalePKs, unselectedSalePKs, isSelectAll } = this.state
        if (isSelectAll) {
            return !unselectedSalePKs.has(salePK)
        }
        return selectedSalePKs.has(salePK)
    }

    handleFilterValuesChange = (values) => {
        const newFilters = { ...this.state.filterValues, ...values }
        this.setState({ filterValues: newFilters })
        this.fetchSaleDataDebounced(this.getDataURL(), true, 0, newFilters)
    }

    handleFilterChange = (field) => {
        return (option) => {
            if (this.salesContainerRef.current) {
                // reset scroll to top
                this.salesContainerRef.current.scrollTop = 0
            }
            const filterValues = { ...this.state.filterValues }
            if (option) {
                filterValues[field.lookup] = option
            } else {
                delete filterValues[field.lookup]
            }

            this.setState({ filterValues, page: 0 }, () => {
                this.fetchSaleData(this.getDataURL(), true, 0)
            })
        }
    }

    clearFilters = (refresh = true) => {
        const filterValues = { ...this.state.filterValues }
        const noClear = ['date_created_after', 'date_created_before']
        const textFields = ['search', 'id_value', 'fulfillment_tracking', 'sale_line_items__sku']

        Object.keys(filterValues).forEach((fieldLookup) => {
            if (!noClear.includes(fieldLookup))
                filterValues[fieldLookup] = textFields.includes(fieldLookup) ? '' : null
        })

        this.setState({ filterValues, page: 0 }, () => {
            refresh && this.fetchSaleData(this.getDataURL(), true, 0)
        })
    }

    handleDateTimeChange = (startDate, endDate) => {
        const filterValues = { ...this.state.filterValues }
        filterValues['date_created_after'] = startDate
        filterValues['date_created_before'] = endDate
        this.setState({ filterValues, page: 0 }, () => {
            this.fetchFilterOptions()
            this.fetchSaleData(this.getDataURL(), true, 0)
        })
    }

    handleExportSelectorOpen = () => {
        this.setState({
            exportSelectorOpen: true,
            salesFilter: this.getSalesFilter(),
        })
    }

    handleExportSelectorClose = () => {
        const newState = { exportSelectorOpen: false }
        this.setState(newState)
    }

    handleGenerateInvoiceOpen = () => {
        this.setState({
            generateInvoiceModalOpen: true,
            salesFilter: this.getSalesFilter(),
        })
    }

    handleGenerateInvoiceClose = () => {
        this.setState({ generateInvoiceModalOpen: false })
    }

    handleProcessInsufficientStockOpen = () => {
        this.setState({
            processInsufficientStockModalOpen: true,
            salesFilter: this.getSalesFilter(),
        })
    }

    handleProcessInsufficientStockClose = (shouldRefetch) => {
        this.setState({ processInsufficientStockModalOpen: false })
        if (shouldRefetch === true) {
            this.clearSelectionAndRefetch()
        }
    }

    getExportFormats = () => {
        return this.props.saleCSVFormats
    }

    handleTableScroll = () => {
        const salesContainerRef = this.salesContainerRef
        if (salesContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = salesContainerRef.current
            const scrollPercent = (2 * clientHeight - scrollHeight + scrollTop) / clientHeight
            if (scrollPercent >= 0.6 && !this.state.loading) {
                // triggered after scroll over 60% of the table
                this.loadNextPage()
            }
        }
    }

    getSalesFilter = () => {
        const { selectedSalePKs, unselectedSalePKs } = this.state

        let filter
        if (selectedSalePKs && selectedSalePKs.size > 0) {
            filter = { included_sale_pks: Array.from(selectedSalePKs) }
        } else {
            filter = parse(this.filtersToQueryString(), { parseBooleans: true })
            if (unselectedSalePKs && unselectedSalePKs.size > 0) {
                filter['excluded_sale_pks'] = Array.from(unselectedSalePKs)
            }
        }
        return filter
    }

    getSelectedCount = () => {
        const { selectedSalePKs, unselectedSalePKs, isSelectAll, count } = this.state
        if (isSelectAll) {
            return count - unselectedSalePKs.size
        }
        return selectedSalePKs.size
    }

    handleToggleFulfillmentView = (enable) => {
        this.setState(
            {
                showFulfillmentView: enable,
            },
            () => {
                this.fetchSaleData(this.getDataURL(), true, 0)
            }
        )
    }

    showFulfillmentSplitDialog = (sale) => {
        this.setState({ splitFulfillmentSale: sale })
    }

    closeFulfillmentSplitDialog = (data) => {
        this.setState({ splitFulfillmentSale: null })
        if ('fulfillments' in data) {
            const sales = this.state.sales.map((sale) => {
                if (sale.id === data.sale_pk) {
                    sale.fulfillments = data.fulfillments
                }
                return sale
            })
            this.setState({ sales })
        }
    }

    render() {
        const { classes, staticRoot } = this.props
        const {
            rowsPerPage,
            count,
            sales,
            shippingInfoManagerOpen,
            isSelectAll,
            selectedSalePKs,
            unselectedSalePKs,
            salesFilter,
            exportSelectorOpen,
            generateInvoiceModalOpen,
            processInsufficientStockModalOpen,
            showFulfillmentView,
            splitFulfillmentSale,
            loading,
            page,
            next,
            filterOptions,
            filterValues,
        } = this.state
        const hasMultiplePages = count > rowsPerPage
        const salesToDisplay = sales

        const currentTime = moment()

        let shipSelectedSalesEnabled = false
        if (isSelectAll) {
            shipSelectedSalesEnabled = pendingStatuses.has(filterValues.status?.value)
        } else {
            shipSelectedSalesEnabled =
                selectedSalePKs.size > 0 &&
                sales
                    .filter((sale) => selectedSalePKs.has(sale.id))
                    .every((sale) => pendingStatuses.has(sale.status))
        }

        let processInsufficientStockSalesEnabled = false
        if (isSelectAll) {
            processInsufficientStockSalesEnabled =
                filterValues.status?.value == 'insufficient_stock'
        } else {
            processInsufficientStockSalesEnabled =
                selectedSalePKs.size > 0 &&
                sales
                    .filter((sale) => selectedSalePKs.has(sale.id))
                    .every((sale) => sale.status == 'insufficient_stock')
        }

        const shipAllCurrentFilterEnabled = pendingStatuses.has(filterValues.status?.value)

        const isLastPage = !next
        let selectAllState = 'unchecked'
        if (isSelectAll) {
            selectAllState = unselectedSalePKs.size === 0 ? 'checked' : 'indeterminate'
        }

        return (
            <Paper className={classes.root}>
                {shippingInfoManagerOpen && (
                    <ShippingInfoManager
                        open={shippingInfoManagerOpen}
                        handleCloseShippingInfoManager={this.handleCloseShippingInfoManager}
                        salesFilter={salesFilter}
                    />
                )}
                <SalesTableToolbar
                    numSelected={this.getSelectedCount()}
                    shipSelectedSalesEnabled={shipSelectedSalesEnabled}
                    processInsufficientStockSalesEnabled={processInsufficientStockSalesEnabled}
                    shipAllCurrentFilterEnabled={shipAllCurrentFilterEnabled}
                    stores={this.props.stores}
                    handleShipSelected={this.handleShipSelected}
                    handleExportSelected={this.handleExportSelectorOpen}
                    handleFilterValuesChange={this.handleFilterValuesChange}
                    handleGenerateInvoicesForSelected={this.handleGenerateInvoiceOpen}
                    handleProcessInsufficientStock={this.handleProcessInsufficientStockOpen}
                    numTotal={count}
                    page={page}
                    loading={loading}
                    shippingInfoManagerOpen={shippingInfoManagerOpen}
                    handleOpenShippingInfoManager={this.handleOpenShippingInfoManager}
                    filterOptions={filterOptions}
                    filterValues={filterValues}
                    handleFilterChange={this.handleFilterChange}
                    handleClearFilters={this.clearFilters}
                    handleDateTimeChange={this.handleDateTimeChange}
                    handleClearDateTime={this.handleClearDateTime}
                    handleExportSales={this.handleExportSelectorOpen}
                    handleSort={this.handleSort}
                    showFulfillmentView={showFulfillmentView}
                    onFulfillmentViewChange={this.handleToggleFulfillmentView}
                />
                <TableContainer
                    className={classes.tableContainer}
                    onScroll={this.handleTableScroll}
                    ref={this.salesContainerRef}
                >
                    <Table
                        className={classes.table}
                        aria-labelledby="tableTitle"
                        stickyHeader
                        aria-label="sticky table"
                    >
                        <SaleTableHead
                            className={classes.tableHead}
                            onSelectAllClick={this.handleSelectAllClick}
                            selectAllState={selectAllState}
                        />
                        <TableBody>
                            {!loading && sales.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={9}
                                        className={classNames(
                                            classes.noSalesMessage,
                                            classes.tableCell
                                        )}
                                    >
                                        <img
                                            className={classes.noSalesIcon}
                                            src={staticRoot + 'web/img/order.png'}
                                        />
                                        <Typography
                                            className={classes.noSalesText}
                                            variant="subtitle1"
                                        >
                                            {gettext('No orders to display')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}

                            {salesToDisplay.map((sale, rowIndex) => {
                                const isSelected = this.isSelected(sale.id)
                                const saleTotal = sale.total || 0.0
                                const saleLink = getPath('sale', { sale_id: sale.id })

                                return (
                                    <>
                                        <TableRow
                                            className={classNames({
                                                [classes.saleRowWithFulfillments]:
                                                    showFulfillmentView &&
                                                    sale.fulfillments?.length > 0,
                                            })}
                                            hover
                                            role="checkbox"
                                            aria-checked={isSelected}
                                            tabIndex={-1}
                                            key={sale.id}
                                            selected={isSelected}
                                        >
                                            <TableCell
                                                padding="checkbox"
                                                className={classes.tableCell}
                                            >
                                                <Checkbox
                                                    checked={isSelected}
                                                    onClick={(e) =>
                                                        this.handleCheckboxClick(
                                                            e,
                                                            sale.id,
                                                            rowIndex
                                                        )
                                                    }
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                <Link to={saleLink} className={classes.saleLink}>
                                                    <img
                                                        className={classes.saleImage}
                                                        src={sale.cover_image_url}
                                                        height={40}
                                                    />
                                                </Link>
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                <Link to={saleLink} className={classes.saleLink}>
                                                    <Typography
                                                        className={classes.tableText}
                                                        variant="body2"
                                                    >
                                                        {sale.sale_id}
                                                    </Typography>
                                                </Link>
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                <Link
                                                    to={saleLink}
                                                    className={classes.saleLink}
                                                    target="_blank"
                                                >
                                                    <Typography
                                                        className={classNames(classes.tableText)}
                                                        variant="body2"
                                                    >
                                                        {sale.shipping_address
                                                            ? `${sale.shipping_address.city} - ${sale.shipping_address.province}`
                                                            : `${sale.city} - ${sale.province}`}
                                                    </Typography>
                                                </Link>
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                <Link to={saleLink} className={classes.saleLink}>
                                                    <Typography
                                                        className={classes.tableText}
                                                        variant="body2"
                                                    >
                                                        {sale.shop.shop_name}
                                                    </Typography>
                                                </Link>
                                            </TableCell>
                                            <TableCell
                                                className={classNames(
                                                    classes.saleTotal,
                                                    classes.tableCell
                                                )}
                                            >
                                                <Link to={saleLink} className={classes.saleLink}>
                                                    <Typography
                                                        className={classNames(classes.tableText)}
                                                        variant="body2"
                                                    >
                                                        {currencySymbols[sale.currency] +
                                                            (+saleTotal).toFixed(2)}
                                                    </Typography>
                                                    <Typography
                                                        className={classNames(
                                                            classes.numItems,
                                                            classes.tableText
                                                        )}
                                                        variant="body2"
                                                    >
                                                        {interpolate(
                                                            ngettext(
                                                                '%s item',
                                                                '%s items',
                                                                +sale.items_count
                                                            ),
                                                            [+sale.items_count]
                                                        )}
                                                    </Typography>
                                                </Link>
                                            </TableCell>
                                            <TableCell
                                                className={classNames(
                                                    classes.shippingColumn,
                                                    classes.tableCell
                                                )}
                                            >
                                                <Link to={saleLink} className={classes.saleLink}>
                                                    <Typography className={classes.tableText}>
                                                        {sale.shipping_provider}
                                                    </Typography>

                                                    {!!sale.ship_by_date && (
                                                        <Typography
                                                            component="span"
                                                            className={classNames(
                                                                classes.tableText,
                                                                classes.shipByDate,
                                                                {
                                                                    [classes.shipByDateOverdue]:
                                                                        currentTime >=
                                                                        moment(sale.ship_by_date),
                                                                }
                                                            )}
                                                            variant="body2"
                                                        >
                                                            {gettext('Before')}{' '}
                                                            {moment(sale.ship_by_date).format(
                                                                'D MMM'
                                                            )}
                                                        </Typography>
                                                    )}
                                                </Link>
                                            </TableCell>
                                            <TableCell
                                                className={classNames(
                                                    classes.channel,
                                                    classes.tableCell
                                                )}
                                            >
                                                {sale.is_tax_invoice_requested && (
                                                    <CheckCircleIcon color="secondary" />
                                                )}
                                            </TableCell>
                                            <TableCell
                                                className={classNames(
                                                    classes.status,
                                                    classes.tableCell
                                                )}
                                            >
                                                <Link to={saleLink} className={classes.saleLink}>
                                                    <Typography
                                                        className={classNames(classes.tableText)}
                                                    >
                                                        {sale.status_display_name}
                                                    </Typography>
                                                    <Typography
                                                        className={classNames(
                                                            classes.tableText,
                                                            classes.statusUpdatedTime
                                                        )}
                                                        variant="body2"
                                                    >
                                                        {moment(sale.date_updated).format(
                                                            'D MMM HH:mm'
                                                        )}
                                                    </Typography>
                                                </Link>
                                            </TableCell>
                                            <TableCell
                                                className={classNames(
                                                    classes.channel,
                                                    classes.tableCell
                                                )}
                                            >
                                                <Link to={saleLink} className={classes.saleLink}>
                                                    <img
                                                        className={classes.channelIcon}
                                                        src={sale.channel_square_icon_url}
                                                    />
                                                </Link>
                                            </TableCell>
                                        </TableRow>
                                        {showFulfillmentView && sale.fulfillments && (
                                            <TableRow hover key={`fulfillments_${sale.id}`}>
                                                <TableCell
                                                    colSpan={10}
                                                    style={{
                                                        margin: 0,
                                                        padding: 0,
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    <FulfillmentTable
                                                        data={sale.fulfillments}
                                                        enableSplit={
                                                            sale.status === 'pending' &&
                                                            sale.items_count > 1
                                                        }
                                                        onFulfillmentSplitClick={() =>
                                                            this.showFulfillmentSplitDialog(sale)
                                                        }
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </>
                                )
                            })}

                            {hasMultiplePages && (
                                <TableRow>
                                    <TableCell colSpan={9}>
                                        <div style={{ textAlign: 'center' }}>
                                            {isLastPage ? (
                                                <span>{gettext('All data loaded.')}</span>
                                            ) : (
                                                <CircularProgress size={28} />
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <SaleCSVExportSelector
                    open={exportSelectorOpen}
                    formats={this.getExportFormats()}
                    onComplete={this.handleExportSelectorClose}
                    onClose={this.handleExportSelectorClose}
                    staticRoot={staticRoot}
                    salesFilter={salesFilter}
                />

                <GenerateTaxInvoiceModal
                    open={generateInvoiceModalOpen}
                    onComplete={this.handleGenerateInvoiceClose}
                    onClose={this.handleGenerateInvoiceClose}
                    salesFilter={salesFilter}
                />

                <ProcessInsufficientStockModal
                    open={processInsufficientStockModalOpen}
                    onComplete={this.handleProcessInsufficientStockClose}
                    onClose={this.handleProcessInsufficientStockClose}
                    salesFilter={salesFilter}
                />

                {splitFulfillmentSale && (
                    <FulfillmentSplitDialog
                        open={true}
                        sale={splitFulfillmentSale}
                        onClose={this.closeFulfillmentSplitDialog}
                    />
                )}
            </Paper>
        )
    }
}

export default withStyles(styles)(Sales)
